/**
 * Fusión de parches — la semántica compartida por los dos motores de datos.
 *
 * Escalón E0 del plan de diseño (2026-08-22). Hasta hoy los dos motores de
 * `store.js` NO hacían lo mismo: Firestore escribía con `merge: true` (fusión de
 * mapas, arreglos sustituidos) y `local.js` reemplazaba el documento entero. Dos
 * pestañas editando la misma cotización daban resultado distinto según el motor,
 * y eso es exactamente el tipo de bug que solo aparece en casa del cliente.
 *
 * Esta es la ÚNICA definición de «fusionar» del sistema, y copia a propósito las
 * reglas de `setDoc(…, { merge: true })` de Firestore:
 *   · un objeto plano (mapa) se fusiona clave por clave, recursivamente;
 *   · un arreglo SUSTITUYE al anterior completo (Firestore no fusiona arreglos);
 *   · un valor primitivo sustituye;
 *   · el centinela `CENTINELA_BORRAR` elimina la clave (equivale a `deleteField()`).
 *
 * De aquí sale la regla de diseño de los datos: lo que dos dispositivos pueden
 * editar a la vez vive en MAPAS por id, nunca en arreglos.
 */

/** Marca «borra esta clave». Es un objeto y no un símbolo para que sobreviva a
    JSON (el motor local serializa) y para que Firestore lo traduzca a deleteField. */
export const CENTINELA_BORRAR = Object.freeze({ __borrar__: true })

export const esBorrar = v => !!v && typeof v === 'object' && v.__borrar__ === true

/** Un mapa «plano»: objeto literal, no arreglo, no Date, no clase. */
export const esMapa = v =>
  v !== null && typeof v === 'object' && !Array.isArray(v) &&
  Object.getPrototypeOf(v) === Object.prototype

/** Devuelve un objeto NUEVO: `base` fusionada con `parche`. No muta ninguno. */
export function fusionar(base, parche) {
  const salida = esMapa(base) ? { ...base } : {}
  for (const [clave, valor] of Object.entries(parche || {})) {
    if (valor === undefined) continue              // Firestore rechaza undefined; se ignora
    if (esBorrar(valor)) { delete salida[clave]; continue }
    if (esMapa(valor)) salida[clave] = fusionar(esMapa(salida[clave]) ? salida[clave] : {}, valor)
    else salida[clave] = valor
  }
  return salida
}

/** `desdeRuta('plano.recintos.jardin', v)` → `{ plano: { recintos: { jardin: v } } }`. */
export const desdeRuta = (ruta, valor) =>
  ruta.split('.').reverse().reduce((acumulado, clave) => ({ [clave]: acumulado }), valor)

/** Peso en bytes del documento serializado — la cota que importa a Firestore. */
export const tamanoJSON = objeto => new TextEncoder().encode(JSON.stringify(objeto)).length

/** Tope propio, por debajo del 1 MiB de Firestore, con margen para metadatos. */
export const TOPE_DOCUMENTO = 800_000
